/*
  AIM cookie consent: the banner, the settings dialog, and the hand-off of the
  visitor's choice to everything that tracks.

  Markup, text and settings come from snippets/aim-consent.liquid. The choice
  is kept in the aim_consent cookie, a URL-encoded JSON object:

    v   consent version from Theme settings; changing it asks everyone again
    s   implied | accepted | rejected | custom
    p   { f: functional, a: analytics, m: marketing }, each 0 or 1
    t   time of the choice, ms
    id  random consent id, shown to the visitor for any data request

  Where the choice goes:
    Shopify   Customer Privacy API (setTrackingConsent). Shopify's own pixels,
              the Meta pixel of the Facebook & Instagram app, Omnisend and the
              Microsoft Clarity app embed all read their permission from it.
    Google    Consent Mode v2 updates for the Tag Manager container, whose
              defaults snippets/aim-consent-head.liquid sets before it loads.

  The opt_out model writes an "implied" yes for a first-time visitor, so every
  tag runs from the first page, the way cannumo.lt's Pandectes setup does. The
  strict model leaves everything off until the visitor chooses.

  Page code can read or change the choice through window.AIMConsent, and hear
  about changes through the aim:consent event on document.
*/
(() => {
  const root = document.querySelector('[data-aim-consent]');
  if (!root || root.dataset.ready) return;
  root.dataset.ready = 'true';

  const cfg = JSON.parse(root.querySelector('[data-aim-consent-config]').textContent);
  const banner = root.querySelector('[data-aim-consent-banner]');
  const dialog = root.querySelector('[data-aim-consent-dialog]');
  const live = root.querySelector('[data-aim-consent-status]');
  const meta = root.querySelector('[data-aim-consent-meta]');
  const reopen = root.querySelector('[data-aim-consent-reopen]');
  const switches = [...root.querySelectorAll('[data-aim-consent-cat]')];

  const COOKIE = 'aim_consent';
  const MAX_AGE = 60 * 60 * 24 * 365;
  const ALL = { f: 1, a: 1, m: 1 };
  const NONE = { f: 0, a: 0, m: 0 };
  const KEYS = ['f', 'a', 'm'];
  const strict = cfg.mode === 'strict';

  /* ---------- stored choice ---------- */

  function read() {
    try {
      const match = document.cookie.match(/(?:^|;\s*)aim_consent=([^;]*)/);
      const saved = match && JSON.parse(decodeURIComponent(match[1]));
      return saved && saved.p ? saved : null;
    } catch (e) {
      return null;
    }
  }

  function write(next) {
    const secure = location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${COOKIE}=${encodeURIComponent(JSON.stringify(next))}; Max-Age=${MAX_AGE}; Path=/; SameSite=Lax${secure}`;
  }

  function forget() {
    document.cookie = `${COOKIE}=; Max-Age=0; Path=/; SameSite=Lax`;
  }

  function newId() {
    if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  }

  const clean = (p) => ({ f: p.f ? 1 : 0, a: p.a ? 1 : 0, m: p.m ? 1 : 0 });
  const same = (x, y) => KEYS.every((k) => !!x[k] === !!y[k]);
  const statusOf = (p) => (same(p, ALL) ? 'accepted' : same(p, NONE) ? 'rejected' : 'custom');
  const decided = (s) => !!s && s.s !== 'implied';

  let state = read();

  /* What Google was told in the head before this script ran: the stored
     choice when there is one, otherwise the model's default. */
  const headDefault = decided(state) ? clean(state.p) : strict ? NONE : ALL;
  let effective = headDefault;

  /* ---------- Shopify Customer Privacy API ---------- */

  const privacyReady = new Promise((resolve) => {
    const done = () => resolve((window.Shopify && window.Shopify.customerPrivacy) || null);
    if (window.Shopify && window.Shopify.customerPrivacy) return done();
    if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') return done();
    setTimeout(done, 2500);
    try {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], done);
    } catch (e) {
      done();
    }
  });

  function shopifyNow(api) {
    try {
      return api && api.currentVisitorConsent ? api.currentVisitorConsent() : null;
    } catch (e) {
      return null;
    }
  }

  /* A choice made earlier in Shopify's own cookie banner, or null when the
     visitor never answered it. */
  function shopifyChoice(api) {
    const c = shopifyNow(api);
    if (!c) return null;
    const answered = (v) => v === 'yes' || v === 'no';
    if (![c.preferences, c.analytics, c.marketing].every(answered)) return null;
    return { f: c.preferences === 'yes' ? 1 : 0, a: c.analytics === 'yes' ? 1 : 0, m: c.marketing === 'yes' ? 1 : 0 };
  }

  async function syncShopify(p) {
    const api = await privacyReady;
    if (!api || typeof api.setTrackingConsent !== 'function') return;
    const c = shopifyNow(api);
    const want = (on) => (on ? 'yes' : 'no');
    if (c && c.preferences === want(p.f) && c.analytics === want(p.a) && c.marketing === want(p.m)) return;
    await new Promise((resolve) => {
      setTimeout(resolve, 4000);
      try {
        api.setTrackingConsent({ preferences: !!p.f, analytics: !!p.a, marketing: !!p.m }, resolve);
      } catch (e) {
        resolve();
      }
    });
  }

  /* ---------- Google Consent Mode ---------- */

  function gtag() {
    (window.dataLayer = window.dataLayer || []).push(arguments);
  }

  function tellGoogle(p, status) {
    if (!cfg.google) return;
    const s = (on) => (on ? 'granted' : 'denied');
    gtag('consent', 'update', {
      ad_storage: s(p.m),
      ad_user_data: s(p.m),
      ad_personalization: s(p.m),
      analytics_storage: s(p.a),
      functionality_storage: s(p.f),
      personalization_storage: s(p.f),
    });
    window.dataLayer.push({
      event: 'aim_consent_update',
      aim_consent: { status, functional: !!p.f, analytics: !!p.a, marketing: !!p.m },
    });
  }

  /* ---------- clean-up when a category is switched off ---------- */

  function dropCookie(name) {
    const host = location.hostname;
    const domains = ['', host, `.${host}`, `.${host.replace(/^www\./, '')}`];
    domains.forEach((d) => {
      document.cookie = `${name}=; Max-Age=0; Path=/${d ? `; Domain=${d}` : ''}`;
    });
  }

  function dropStored(name) {
    try {
      localStorage.removeItem(name);
      sessionStorage.removeItem(name);
    } catch (e) {}
  }

  /* Scripts already running stop on the consent update itself; this removes
     what they left behind. Third-party cookies cannot be reached from here and
     expire on their own. */
  function cleanUp(before, after) {
    const lists = { f: cfg.store.functional, a: cfg.store.analytics, m: cfg.store.marketing };
    KEYS.forEach((k) => {
      if (!before[k] || after[k]) return;
      (lists[k].cookies || []).forEach(dropCookie);
      (lists[k].local || []).forEach(dropStored);
    });
  }

  /* ---------- telling the page ---------- */

  function emit() {
    const detail = api.get();
    document.dispatchEvent(new CustomEvent('aim:consent', { detail }));
  }

  function say(text) {
    if (!live) return;
    live.textContent = '';
    setTimeout(() => (live.textContent = text), 60);
  }

  function renderMeta() {
    if (!meta) return;
    if (!decided(state)) {
      meta.hidden = true;
      return;
    }
    let when = '';
    try {
      when = new Date(state.t).toLocaleDateString(document.documentElement.lang || undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch (e) {}
    meta.querySelector('[data-aim-consent-id]').textContent = state.id;
    meta.querySelector('[data-aim-consent-date]').textContent = when;
    meta.hidden = false;
  }

  /* ---------- banner ---------- */

  function showBanner() {
    banner.hidden = false;
    if (reopen) reopen.hidden = true;
    requestAnimationFrame(() => requestAnimationFrame(() => banner.classList.add('is-in')));
  }

  function hideBanner() {
    banner.classList.remove('is-in');
    banner.hidden = true;
    if (reopen) reopen.hidden = false;
  }

  /* ---------- settings dialog ---------- */

  function fillSwitches() {
    switches.forEach((input) => {
      input.checked = !!effective[input.dataset.aimConsentCat];
    });
  }

  function openDialog() {
    if (!dialog) return;
    fillSwitches();
    renderMeta();
    if (!dialog.open) {
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
    }
    const body = dialog.querySelector('.aim-consent__body');
    if (body) body.scrollTop = 0;
  }

  function closeDialog() {
    if (!dialog || !dialog.open) return;
    if (typeof dialog.close === 'function') dialog.close();
    else dialog.removeAttribute('open');
  }

  function fromSwitches() {
    const p = { ...NONE };
    switches.forEach((input) => {
      p[input.dataset.aimConsentCat] = input.checked ? 1 : 0;
    });
    return p;
  }

  /* ---------- committing a choice ---------- */

  function commit(next, status) {
    const before = effective;
    next = clean(next);
    state = { v: cfg.version, s: status || statusOf(next), p: next, t: Date.now(), id: (state && state.id) || newId() };
    effective = next;
    hideBanner();
    closeDialog();
    say(cfg.text.saved);
    if (cfg.design) return;
    write(state);
    cleanUp(before, next);
    tellGoogle(next, state.s);
    syncShopify(next);
    emit();
  }

  /* ---------- wiring ---------- */

  function toggleSection(button) {
    const item = button.closest('[data-aim-consent-fold]');
    const open = button.getAttribute('aria-expanded') !== 'true';
    button.setAttribute('aria-expanded', String(open));
    item.classList.toggle('is-open', open);
  }

  root.addEventListener('click', (event) => {
    const action = event.target.closest('[data-aim-consent-action]');
    if (action) {
      event.preventDefault();
      switch (action.dataset.aimConsentAction) {
        case 'accept':
          commit(ALL);
          break;
        case 'reject':
          commit(NONE);
          break;
        case 'save':
          commit(fromSwitches());
          break;
        case 'prefs':
          openDialog();
          break;
        case 'close':
          closeDialog();
          break;
      }
      return;
    }
    const fold = event.target.closest('[data-aim-consent-fold-toggle]');
    if (fold) toggleSection(fold);
  });

  /* A click on the backdrop lands on the dialog element itself. */
  if (dialog) {
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) closeDialog();
    });
  }

  /* Anything on the site can open the settings: the footer link, a link to
     #cookie-settings typed into a page, or any element carrying the attribute. */
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-aim-consent-open], a[href$="#cookie-settings"]');
    if (!trigger) return;
    event.preventDefault();
    openDialog();
  });

  const api = {
    get: () => ({
      status: state ? state.s : null,
      functional: !!effective.f,
      analytics: !!effective.a,
      marketing: !!effective.m,
      id: state ? state.id : null,
    }),
    open: openDialog,
    set: (prefs) =>
      commit({
        f: prefs.functional ?? effective.f,
        a: prefs.analytics ?? effective.a,
        m: prefs.marketing ?? effective.m,
      }),
  };
  window.AIMConsent = api;

  /* ---------- start ---------- */

  async function start() {
    if (cfg.design) {
      showBanner();
      return;
    }

    if (decided(state)) {
      effective = clean(state.p);
      syncShopify(effective);
      if (state.v !== cfg.version) showBanner();
      else hideBanner();
      return;
    }

    /* Strict model after an opt_out period: take back the implied yes. */
    if (strict && state && state.s === 'implied') {
      forget();
      state = null;
      syncShopify(NONE);
    }

    /* Someone who already answered Shopify's own banner keeps that answer and
       is not asked again. */
    if (!state) {
      const prior = shopifyChoice(await privacyReady);
      if (prior) {
        state = { v: cfg.version, s: statusOf(prior), p: prior, t: Date.now(), id: newId() };
        write(state);
        if (!same(prior, headDefault)) tellGoogle(prior, state.s);
        effective = prior;
        hideBanner();
        emit();
        return;
      }
    }

    if (!strict) {
      if (!state) {
        state = { v: cfg.version, s: 'implied', p: ALL, t: Date.now(), id: newId() };
        write(state);
      }
      effective = ALL;
      syncShopify(ALL);
    }

    showBanner();
  }

  start().then(() => {
    if (location.hash === '#cookie-settings') openDialog();
  });
})();
